import { SchemaObject } from '@augloop/types-core';
import { Log, LogCategory, Operation as LogOperation } from '@augloop/logging';
import { TextTileDelta, TextTileDeltaType, TextTile, FormattedTextTile, TextTileDeltaUnit } from '@augloop/types-text';
const nonContentDeltaTypes = [TextTileDeltaType.CursorUpdate, TextTileDeltaType.FormattingUpdate, TextTileDeltaType.OtherNonContentUpdate];
export function applyTextTileDelta(delta, tile, _clientMetadataFlights) {
    const applyDeltaOp = new LogOperation({
        operationName: 'ApplyTextTileDeltaForLocalWorkflows',
        dimension0: '0',
        success: true
    }).start();
    try {
        if (!tile) {
            applyDeltaOp.success = false;
            applyDeltaOp.resultDescription = `Unable to apply text tile delta, parent tile is undefined`;
            Log.info(0x201d685d /* tag_6hw73 */, LogCategory.CoreDefault, applyDeltaOp.stop());
            return undefined;
        }
        if (SchemaObject.getTypeNameFor(tile) !== TextTile.getTypeName()) {
            applyDeltaOp.success = false;
            applyDeltaOp.resultDescription = `Unable to apply text tile delta, parent tile is not proper type: expected ${TextTileDelta.getTypeName()}, received ${SchemaObject.getTypeNameFor(tile)}`;
            Log.info(0x201d685e /* tag_6hw74 */, LogCategory.CoreDefault, applyDeltaOp.stop());
            return undefined;
        }
        const textTile = tile;
        const textTileDelta = delta;
        const content = textTile.content;
        let updatedContent = '';
        if (textTileDelta.position === undefined || textTileDelta.position < 0) {
            applyDeltaOp.success = false;
            applyDeltaOp.resultDescription = `Unable to apply text tile delta, invalid text tile position`;
            Log.info(0x201d685f /* tag_6hw75 */, LogCategory.CoreDefault, applyDeltaOp.stop());
            return undefined;
        }
        switch (textTileDelta.deltaType) {
            case TextTileDeltaType.Add:
                if (textTileDelta.position < content.length) {
                    updatedContent = `${content.slice(0, textTileDelta.position)}${textTileDelta.content}${content.slice(textTileDelta.position, content.length)}`;
                }
                else {
                    updatedContent = content + textTileDelta.content;
                }
                break;
            case TextTileDeltaType.Update:
                if (!textTileDelta.content && textTileDelta.unit === TextTileDeltaUnit.Sentence) {
                    applyDeltaOp.dimension0 = '1';
                }
                updatedContent = `${content.slice(0, textTileDelta.position)}${textTileDelta.content}${content.slice(textTileDelta.position + (textTileDelta.length || 0), content.length)}`;
                break;
            case TextTileDeltaType.Delete:
                updatedContent = `${content.slice(0, textTileDelta.position)}${content.slice(textTileDelta.position + (textTileDelta.length || 0), content.length)}`;
        }
        Log.info(0x201e004f /* tag_6h6bp */, LogCategory.CoreDefault, applyDeltaOp.stop());
        return new TextTile({
            content: updatedContent
        });
    }
    catch (error) {
        applyDeltaOp.success = false;
        applyDeltaOp.resultDescription = `Error applying text tile delta: ${error}`;
        Log.info(0x201d6860 /* tag_6hw76 */, LogCategory.CoreDefault, applyDeltaOp.stop());
        return undefined;
    }
}
export function applyFormattedTextTileDelta(delta, tile, clientMetadataFlights) {
    var _a, _b, _c, _d, _e, _f, _g, _h;
    const applyDeltaOp = new LogOperation({
        operationName: 'ApplyFormattedTextTileDeltaForLocalWorkflows',
        dimension0: '0',
        success: true
    }).start();
    // This is temporary just to measure some ratio of differente client settings for this feature
    // Will be removed once we have enough data, already added to cleanup PR: https://dev.azure.com/office/ISS/_git/augloop-server/pullrequest/1723332
    applyDeltaOp.clientFlights = clientMetadataFlights;
    try {
        if (!tile) {
            applyDeltaOp.success = false;
            applyDeltaOp.resultDescription = 'Unable to apply formatted text tile delta, parent tile is undefined';
            Log.info(0x201d6861 /* tag_6hw77 */, LogCategory.CoreDefault, applyDeltaOp.stop());
            return undefined;
        }
        if (SchemaObject.getTypeNameFor(tile) !== FormattedTextTile.getTypeName()) {
            applyDeltaOp.success = false;
            applyDeltaOp.resultDescription = `Unable to apply formatted text tile delta, parent tile is not proper type: expected ${FormattedTextTile.getTypeName()}, received ${SchemaObject.getTypeNameFor(tile)}`;
            Log.info(0x201d6862 /* tag_6hw78 */, LogCategory.CoreDefault, applyDeltaOp.stop());
            return undefined;
        }
        const textTile = tile;
        const textTileDelta = delta;
        const content = textTile.content;
        let updatedContent = '';
        if ((textTileDelta.position === undefined || textTileDelta.position < 0) && !isNonContentOrDeleteDelta(textTileDelta.deltaType)) {
            applyDeltaOp.success = false;
            applyDeltaOp.resultDescription = 'Unable to apply formatted text tile delta, invalid text tile position';
            Log.info(0x201d6863 /* tag_6hw79 */, LogCategory.CoreDefault, applyDeltaOp.stop());
            return undefined;
        }
        else if (textTileDelta.content === undefined && !isNonContentOrDeleteDelta(textTileDelta.deltaType)) {
            applyDeltaOp.success = false;
            applyDeltaOp.resultDescription = 'Unable to apply formatted text tile delta, non-delete, non-formatting, non-other-non-content-update and non-cursor-update operation without content defined';
            Log.info(0x201d6880 /* tag_6hw8a */, LogCategory.CoreDefault, applyDeltaOp.stop());
            return undefined;
        }
        if (textTileDelta.deltaType === TextTileDeltaType.Add && textTileDelta.length !== 0) {
            Log.info(0x201d6881 /* tag_6hw8b */, LogCategory.CoreDefault, new LogOperation({ operationName: 'ApplyDeltaChecks', resultDescription: 'Received formatted text tile delta add operation with delta length, expected length 0', success: true }));
        }
        else if (isNonContentOrDeleteDelta(textTileDelta.deltaType) && textTileDelta.content !== undefined) {
            Log.info(0x201d6882 /* tag_6hw8c */, LogCategory.CoreDefault, new LogOperation({ operationName: 'ApplyDeltaChecks', resultDescription: 'Received formatted text tile delta delete or non-content related operation with content defined, expected undefined', success: true }));
        }
        if (!isNonContentDelta(textTileDelta.deltaType)) {
            const range = findRangeForDeltaType(textTile.formattedRanges, textTileDelta.deltaType, textTileDelta.position);
            let updatedRanges = textTile.formattedRanges ? [...textTile.formattedRanges] : [];
            // If a range has been found, identify the index of it in the list of ranges
            const index = range ? updatedRanges.indexOf(range) : -1;
            // If the delta happened within a formatted range, adjust the range length
            if (index !== -1) {
                // Operation extends beyond this formatted range
                if (textTileDelta.position + (textTileDelta.length || 0) > range.start + range.length) {
                    range.length = textTileDelta.position + ((_a = textTileDelta.content) !== null && _a !== void 0 ? _a : '').length - range.start;
                }
                else {
                    range.length += ((_b = textTileDelta.content) !== null && _b !== void 0 ? _b : '').length - (textTileDelta.length || 0);
                }
                updatedRanges[index] = range;
            }
            let updatedIpPosition = textTile.ipPosition;
            // Apply content changes and new IP position
            switch (textTileDelta.deltaType) {
                case TextTileDeltaType.Add:
                    if (textTileDelta.position < content.length) {
                        updatedContent = `${content.slice(0, textTileDelta.position)}${textTileDelta.content}${content.slice(textTileDelta.position, content.length)}`;
                    }
                    else {
                        updatedContent = content + textTileDelta.content;
                    }
                    updatedIpPosition = textTileDelta.position + textTileDelta.length;
                    break;
                case TextTileDeltaType.Update:
                    if (!textTileDelta.content && textTileDelta.unit === TextTileDeltaUnit.Sentence) {
                        applyDeltaOp.dimension0 = '1';
                    }
                    updatedContent = `${content.slice(0, textTileDelta.position)}${textTileDelta.content}${content.slice(textTileDelta.position + ((_c = textTileDelta.length) !== null && _c !== void 0 ? _c : 0), content.length)}`;
                    updatedIpPosition = textTileDelta.position + textTileDelta.content.length;
                    break;
                case TextTileDeltaType.Delete:
                    updatedContent = `${content.slice(0, textTileDelta.position)}${content.slice(textTileDelta.position + ((_d = textTileDelta.length) !== null && _d !== void 0 ? _d : 0), content.length)}`;
                    updatedIpPosition = textTileDelta.position;
                    break;
            }
            // Shift any updated ranges that came after the updated range
            for (const updatedRange of updatedRanges.slice(index + 1)) {
                if (updatedRange.start < textTileDelta.position) {
                    continue;
                }
                // Operation extended into (or beyond) current range
                if (textTileDelta.position + (textTileDelta.length || 0) > updatedRange.start) {
                    updatedRange.length = (updatedRange.start + updatedRange.length) - (textTileDelta.position + (textTileDelta.length || 0));
                    updatedRange.start = textTileDelta.position + ((_e = textTileDelta.content) !== null && _e !== void 0 ? _e : '').length;
                }
                else {
                    updatedRange.start += ((_f = textTileDelta.content) !== null && _f !== void 0 ? _f : '').length - (textTileDelta.length || 0);
                }
            }
            // Remove any ranges with zero-length (or less) after a delete or update operation
            updatedRanges = updatedRanges.filter((range) => range.length > 0);
            // Consolidate to return new FormattedTextTile({...}) after logging removed.
            const returnTile = new FormattedTextTile(Object.assign(Object.assign({}, textTile), { ipPosition: updatedIpPosition, content: updatedContent, formattedRanges: updatedRanges, queryRange: textTileDelta.queryRange }));
            Log.info(0x201e0051 /* tag_6h6br */, LogCategory.CoreDefault, applyDeltaOp.stop());
            return returnTile;
        }
        else if (textTileDelta.deltaType === TextTileDeltaType.CursorUpdate) {
            return new FormattedTextTile(Object.assign(Object.assign({}, textTile), { ipPosition: (_g = textTileDelta.cursorData) === null || _g === void 0 ? void 0 : _g.ipPosition, isColdIp: (_h = textTileDelta.cursorData) === null || _h === void 0 ? void 0 : _h.isColdIp }));
        }
        else if (textTileDelta.deltaType === TextTileDeltaType.FormattingUpdate) {
            return new FormattedTextTile(Object.assign(Object.assign({}, textTile), { formattedRanges: textTileDelta.formattedRanges }));
        }
        else if (textTileDelta.deltaType === TextTileDeltaType.OtherNonContentUpdate) {
            let property;
            const diff = {};
            for (property in textTileDelta.otherNonContentData) {
                diff[property] = textTileDelta.otherNonContentData[property];
            }
            return new FormattedTextTile(Object.assign(Object.assign({}, textTile), diff));
        }
        return undefined;
    }
    catch (error) {
        applyDeltaOp.success = false;
        applyDeltaOp.resultDescription = `Error applying formatted text tile delta: ${error}`;
        Log.info(0x201d6883 /* tag_6hw8d */, LogCategory.CoreDefault, applyDeltaOp.stop());
        return undefined;
    }
}
export function findRangeForDeltaType(ranges, deltaType, position) {
    const findRangeLogOp = new LogOperation({
        operationName: 'FindRangeForDelta',
        success: true
    });
    findRangeLogOp.start();
    // Check if there are any ranges to look through
    if (!ranges) {
        findRangeLogOp.resultDescription = 'No formatted ranges found within the tile, skipping.';
        Log.info(0x201d6884 /* tag_6hw8e */, LogCategory.CoreDefault, findRangeLogOp.stop());
        return undefined;
    }
    // Add operations generally look to the position one before them when determining what formatted
    // range to extend, the edge cases being the start of a tile (position 0) or the start of a zero-length formatted range.
    // Check for these edge cases
    if (deltaType === TextTileDeltaType.Add) {
        const zeroLengthRangeAtPosition = ranges.find((range) => range.start === position && range.length === 0);
        if (zeroLengthRangeAtPosition) {
            findRangeLogOp.resultDescription = `Found a zero-length formatted range for add operation with start ${zeroLengthRangeAtPosition.start}.`;
            Log.info(0x201d6885 /* tag_6hw8f */, LogCategory.CoreDefault, findRangeLogOp.stop());
            return zeroLengthRangeAtPosition;
        }
        position = Math.max(position - 1, 0);
    }
    // Check for the range wherein the position is between (inclusive) the start and end of the range
    const foundRange = ranges.find((range) => {
        if (range.length === 0) {
            return range.start === position;
        }
        return range.start <= position && position < (range.start + range.length);
    });
    if (foundRange) {
        findRangeLogOp.resultDescription = `Updating formatted range for operation with start: ${foundRange.start} and length: ${foundRange.length}`;
        Log.info(0x201d6886 /* tag_6hw8g */, LogCategory.CoreDefault, findRangeLogOp.stop());
    }
    else {
        findRangeLogOp.resultDescription = `Unable to find formatted range for operation at position ${position}, skipping.`;
        Log.info(0x201d6887 /* tag_6hw8h */, LogCategory.CoreDefault, findRangeLogOp.stop());
    }
    return foundRange;
}
function isNonContentDelta(deltaType) {
    return nonContentDeltaTypes.indexOf(deltaType) !== -1;
}
function isNonContentOrDeleteDelta(deltaType) {
    return isNonContentDelta(deltaType) || TextTileDeltaType.Delete === deltaType;
}
//# sourceMappingURL=delta-handlers.js.map